import { Megaphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/ui/button/Button';
import { Spinner } from '@shared/components/ui/Spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { cn } from '@shared/utils/utils';
import type { Popup } from '../../models/Popup';
import { getPopupLocalizedContent } from '../../utils/popupLocale';

interface PopupModalProps {
  popup: Popup;
  isVisible: boolean;
  currentIndex: number;
  totalCount: number;
  onNext: () => void;
  isLoading?: boolean;
}

function PopupProgressDots({ currentIndex, totalCount }: { currentIndex: number; totalCount: number }) {
  if (totalCount <= 1) return null;

  return (
    <div
      className="flex items-center justify-center gap-1.5 pt-1"
      role="progressbar"
      aria-valuenow={currentIndex}
      aria-valuemin={1}
      aria-valuemax={totalCount}
    >
      {Array.from({ length: totalCount }, (_, index) => {
        const step = index + 1;
        const isActive = step === currentIndex;

        return (
          <span
            key={step}
            className={cn(
              'h-1.5 rounded-full transition-all duration-200',
              isActive ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30',
            )}
            aria-hidden
          />
        );
      })}
    </div>
  );
}

export const PopupModal = ({
  popup,
  isVisible,
  currentIndex,
  totalCount,
  onNext,
  isLoading = false,
}: PopupModalProps) => {
  const { t, i18n } = useTranslation('popups');

  const { title, description } = getPopupLocalizedContent(popup, i18n.resolvedLanguage ?? i18n.language);

  return (
    <Dialog open={isVisible} onOpenChange={() => undefined}>
      <DialogContent
        className="max-w-md gap-0 overflow-hidden border-border bg-background p-0 sm:max-w-md [&>button]:hidden"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        data-testid="popup-consumer-modal"
        aria-describedby="popup-consumer-description"
      >
        <div className="border-b border-border bg-muted/30 px-6 pb-5 pt-6">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-background shadow-sm">
              <Megaphone className="h-5 w-5 text-primary" aria-hidden />
            </div>

            <DialogHeader className="min-w-0 flex-1 space-y-2 text-left">
              <div className="flex flex-wrap items-center gap-2">
                <DialogTitle className="text-xl font-semibold leading-snug tracking-tight text-foreground">
                  {title}
                </DialogTitle>
                {totalCount > 1 && (
                  <span className="shrink-0 rounded-full border border-border bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {t('consumer.counter', { current: currentIndex, total: totalCount })}
                  </span>
                )}
              </div>
              <PopupProgressDots currentIndex={currentIndex} totalCount={totalCount} />
            </DialogHeader>
          </div>
        </div>

        <div className="max-h-[min(50vh,320px)] overflow-y-auto px-6 py-5">
          <DialogDescription id="popup-consumer-description" asChild>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{description}</p>
          </DialogDescription>
        </div>

        <DialogFooter className="border-t border-border bg-muted/20 px-6 py-4 sm:justify-stretch">
          <Button
            type="button"
            variant="primary"
            size="lg"
            className="w-full sm:w-full"
            onClick={onNext}
            disabled={isLoading}
            data-testid="popup-consumer-understood-button"
          >
            {isLoading ? <Spinner size="sm" /> : t('consumer.understood')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
